import type { Integration, IntegrationCategory } from '@emailforge/schemas';
import { Button, Chip, Input, useDialog } from '@emailforge/ui';
import { useMemo, useState } from 'react';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  type ConnectResult,
  useConnectIntegrationMutation,
  useDisconnectIntegrationMutation,
  useMarketplaceQuery,
  useOauthInitiateMutation,
} from '@/features/integrations/integrationsApi';
import { t } from '@/lib/i18n';

const CATEGORY_LABELS: Record<IntegrationCategory, string> = {
  ecommerce: 'Ecommerce',
  crm: 'CRM',
  payments: 'Payments',
  analytics: 'Analytics',
  data_warehouse: 'Data warehouse',
  forms: 'Forms & data',
  support: 'Support',
  productivity: 'Productivity',
  auth: 'Auth',
  custom: '1st-party / custom',
};

export function IntegrationsMarketplace() {
  const { data, isLoading } = useMarketplaceQuery();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<IntegrationCategory | 'all'>('all');
  const [activeIntegration, setActiveIntegration] = useState<Integration | null>(null);

  const filtered = useMemo(() => {
    const all = data?.integrations ?? [];
    return all
      .filter((i) => activeCategory === 'all' || i.category === activeCategory)
      .filter((i) =>
        !query ? true : i.name.toLowerCase().includes(query.toLowerCase()) || i.slug.includes(query.toLowerCase()),
      );
  }, [data, activeCategory, query]);

  const grouped = useMemo(() => {
    const out: Partial<Record<IntegrationCategory, typeof filtered>> = {};
    for (const item of filtered) {
      const list = out[item.category] ?? [];
      list.push(item);
      out[item.category] = list;
    }
    return out;
  }, [filtered]);

  return (
    <>
      <TopBar
        crumbs={[t('crumbs.configure'), t('nav.integrations')]}
        right={
          <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {data?.integrations.length ?? 0} connectors
          </span>
        }
      />

      <div style={{ flex: 1, overflowY: 'auto', background: 'var(--paper-2)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
          <h1 className="display" style={{ fontSize: 32, margin: '0 0 8px' }}>
            Integration marketplace
          </h1>
          <p style={{ color: 'var(--ink-2)', fontSize: 14, margin: '0 0 20px' }}>
            38 connectors powered by n8n. Trigger sends on events from your ecommerce, CRM, billing or data stack.
          </p>

          <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: 360 }}>
              <Input
                
                placeholder="Search integrations…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                style={{ paddingLeft: 30 }} />
              <div style={{ position: 'absolute', left: 8, top: 7, color: 'var(--ink-3)' }}>
                <Icon.search size={14} />
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 28 }}>
            <CategoryChip
              label="All"
              active={activeCategory === 'all'}
              onClick={() => setActiveCategory('all')}
            />
            {(data?.categories ?? []).map((c) => (
              <CategoryChip
                key={c}
                label={CATEGORY_LABELS[c]}
                active={activeCategory === c}
                onClick={() => setActiveCategory(c)}
              />
            ))}
          </div>

          {isLoading && (
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              Loading catalog…
            </div>
          )}

          {Object.entries(grouped).map(([category, items]) => (
            <section key={category} style={{ marginBottom: 32 }}>
              <div
                className="mono"
                style={{
                  fontSize: 11,
                  textTransform: 'uppercase',
                  letterSpacing: 0.08,
                  color: 'var(--ink-3)',
                  marginBottom: 10,
                }}
              >
                {CATEGORY_LABELS[category as IntegrationCategory]} · {items?.length ?? 0}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 10,
                }}
              >
                {items?.map((i) => (
                  <IntegrationCard
                    key={i.slug}
                    integration={i}
                    onClick={() => setActiveIntegration(i)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>

      {activeIntegration && (
        <ConnectDialog
          integration={activeIntegration}
          onClose={() => setActiveIntegration(null)}
        />
      )}
    </>
  );
}

function IntegrationCard({
  integration,
  onClick,
}: {
  integration: Integration;
  onClick: () => void;
}) {
  const isConnected = integration.status === 'connected';
  const isPending = integration.status === 'pending';

  return (
    <div className="mf-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r-2)',
            background: 'var(--paper-2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
          }}
        >
          {integration.logoEmoji ?? '🔌'}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 550 }}>{integration.name}</div>
            {isConnected && (
              <Chip variant="live"  style={{ fontSize: 9 }}>connected
              </Chip>
            )}
            {isPending && (
              <Chip variant="flame"  style={{ fontSize: 9 }}>pending
              </Chip>
            )}
          </div>
          <div
            className="mono"
            style={{
              fontSize: 10,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.06,
            }}
          >
            {integration.authType.replace('_', ' ')}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 12, color: 'var(--ink-2)', lineHeight: 1.5, marginBottom: 10 }}>
        {integration.description}
      </div>
      <Button
        variant={isConnected ? 'ghost' : undefined}
        size="sm"
        onClick={onClick}
        style={{ width: '100%', justifyContent: 'center' }}
      >
        {isConnected ? 'Manage' : isPending ? 'Resume' : 'Connect'}
      </Button>
    </div>
  );
}

function ConnectDialog({
  integration,
  onClose,
}: {
  integration: Integration;
  onClose: () => void;
}) {
  const [connect, connectMut] = useConnectIntegrationMutation();
  const [disconnect, disconnectMut] = useDisconnectIntegrationMutation();
  const [initiate, initiateMut] = useOauthInitiateMutation();
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ConnectResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [oauthUrl, setOauthUrl] = useState<string | null>(null);
  const [oauthNote, setOauthNote] = useState<string | null>(null);
  const { confirm } = useDialog();

  const isConnected = integration.status === 'connected';

  const fields = credentialFieldsFor(integration.slug);

  const submit = async () => {
    setError(null);
    setResult(null);
    setOauthUrl(null);
    try {
      if (integration.authType === 'oauth') {
        const r = await initiate(integration.slug).unwrap();
        setOauthUrl(r.url);
        setOauthNote(r.note);
      } else if (integration.authType === 'api_key') {
        const missing = fields.filter((f) => !credentials[f.key]);
        if (missing.length > 0) {
          setError(`Missing: ${missing.map((f) => f.label).join(', ')}`);
          return;
        }
        const r = await connect({
          slug: integration.slug,
          input: { authType: 'api_key', credentials },
        }).unwrap();
        setResult(r);
      } else if (integration.authType === 'webhook' || integration.authType === 'built_in') {
        const r = await connect({
          slug: integration.slug,
          input: { authType: integration.authType },
        }).unwrap();
        setResult(r);
      }
    } catch (e: unknown) {
      setError((e as { data?: { message?: string } })?.data?.message ?? 'Could not connect.');
    }
  };

  const remove = async () => {
    if (!await confirm({
      title: `Disconnect ${integration.name}?`,
      description: 'Stored credentials will be removed. You can reconnect any time — the integration will be in "not connected" state.',
      confirmLabel: 'Disconnect',
      destructive: true,
    })) return;
    try {
      await disconnect(integration.slug).unwrap();
      onClose();
    } catch (e: unknown) {
      setError((e as { data?: { message?: string } })?.data?.message ?? 'Could not disconnect.');
    }
  };

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      // ignore
    }
  };

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 40 }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 520,
          maxWidth: '92vw',
          maxHeight: '86vh',
          background: 'var(--paper)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--r-3)',
          boxShadow: '0 16px 48px rgba(0,0,0,0.18)',
          zIndex: 41,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--line-soft)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 22 }}>{integration.logoEmoji ?? '🔌'}</span>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{integration.name}</div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>
              {integration.authType.replace('_', ' ')}
              {isConnected && ' · connected'}
            </div>
          </div>
          <div style={{ flex: 1 }} />
          <Button variant="ghost" size="sm" icon onClick={onClose} aria-label="Close">
            <Icon.x size={12} />
          </Button>
        </div>

        <div style={{ padding: 18, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            {integration.description}
          </div>

          {integration.authType === 'oauth' && !oauthUrl && (
            <div
              style={{
                padding: 12,
                background: 'var(--paper-2)',
                border: '1px solid var(--line-soft)',
                borderRadius: 'var(--r-2)',
                fontSize: 12.5,
                color: 'var(--ink-2)',
              }}
            >
              You'll be redirected to {integration.name} to authorize access. We never see your password — only the
              scoped tokens you grant.
            </div>
          )}

          {integration.authType === 'oauth' && oauthUrl && (
            <div
              style={{
                padding: 12,
                background: 'var(--flame-soft)',
                border: '1px solid var(--flame-line)',
                borderRadius: 'var(--r-2)',
                fontSize: 12.5,
                color: 'var(--flame-2)',
              }}
            >
              <div style={{ fontWeight: 550, marginBottom: 4 }}>OAuth not yet wired in dev</div>
              <div style={{ fontSize: 12 }}>{oauthNote}</div>
            </div>
          )}

          {integration.authType === 'api_key' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {fields.map((f) => (
                <div key={f.key}>
                  <label
                    className="mono"
                    style={{
                      fontSize: 10.5,
                      textTransform: 'uppercase',
                      letterSpacing: 0.06,
                      color: 'var(--ink-3)',
                      display: 'block',
                      marginBottom: 6,
                    }}
                  >
                    {f.label}
                  </label>
                  <Input
                    
                    type={f.secret ? 'password' : 'text'}
                    placeholder={f.placeholder}
                    value={credentials[f.key] ?? ''}
                    onChange={(e) => setCredentials({ ...credentials, [f.key]: e.target.value })} />
                </div>
              ))}
            </div>
          )}

          {(integration.authType === 'webhook' || integration.authType === 'built_in') && result && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SecretBlock
                label="Webhook URL"
                value={result.webhookUrl ?? ''}
                onCopy={() => result.webhookUrl && copy(result.webhookUrl)}
              />
              <SecretBlock
                label="Signing secret"
                value={result.signingSecret ?? ''}
                onCopy={() => result.signingSecret && copy(result.signingSecret)}
                mask
              />
              <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                Configure {integration.name} to POST to the URL above. We verify each request using HMAC-SHA256 with
                the signing secret.
              </div>
            </div>
          )}

          {(integration.authType === 'webhook' || integration.authType === 'built_in') && !result && (
            <div style={{ fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
              Click Connect to generate an inbound URL + signing secret. {integration.name} will POST to that URL
              with each event.
            </div>
          )}

          {error && (
            <div
              style={{
                padding: 10,
                background: 'var(--flame-soft)',
                border: '1px solid var(--flame-line)',
                borderRadius: 'var(--r-2)',
                color: 'var(--flame-2)',
                fontSize: 12.5,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--line-soft)',
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}
        >
          {isConnected && (
            <Button size="sm" onClick={remove} disabled={disconnectMut.isLoading}>
              <Icon.trash size={12} />
              {disconnectMut.isLoading ? 'Disconnecting…' : 'Disconnect'}
            </Button>
          )}
          <Button onClick={onClose}>
            Close
          </Button>
          {!isConnected && (
            <Button variant="flame" onClick={submit}
              disabled={connectMut.isLoading || initiateMut.isLoading}>
              {connectMut.isLoading || initiateMut.isLoading
                ? 'Connecting…'
                : integration.authType === 'oauth'
                  ? 'Get authorization URL'
                  : 'Connect'}
            </Button>
          )}
        </div>
      </div>
    </>
  );
}

function SecretBlock({
  label,
  value,
  onCopy,
  mask,
}: {
  label: string;
  value: string;
  onCopy: () => void;
  mask?: boolean;
}) {
  const [revealed, setRevealed] = useState(!mask);
  const display = revealed ? value : '•'.repeat(Math.min(28, value.length));
  return (
    <div>
      <div
        className="mono"
        style={{
          fontSize: 10.5,
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          color: 'var(--ink-3)',
          display: 'block',
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '8px 10px',
          background: 'var(--paper-3)',
          border: '1px solid var(--line-soft)',
          borderRadius: 'var(--r-2)',
        }}
      >
        <span
          className="mono"
          style={{
            flex: 1,
            fontSize: 11.5,
            color: 'var(--ink)',
            wordBreak: 'break-all',
          }}
        >
          {display}
        </span>
        {mask && (
          <Button variant="ghost" size="sm" icon onClick={() => setRevealed((v) => !v)} aria-label="Toggle">
            <Icon.eye size={12} />
          </Button>
        )}
        <Button variant="ghost" size="sm" icon onClick={onCopy} aria-label="Copy">
          <Icon.copy size={12} />
        </Button>
      </div>
    </div>
  );
}

function CategoryChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Button size="sm" onClick={onClick}
      style={{
        background: active ? 'var(--ink)' : 'var(--paper)',
        color: active ? 'var(--paper)' : 'var(--ink-2)',
        borderColor: active ? 'var(--ink)' : 'var(--line)',
      }}>
      {label}
    </Button>
  );
}

type CredentialField = { key: string; label: string; placeholder?: string; secret?: boolean };

function credentialFieldsFor(slug: string): CredentialField[] {
  switch (slug) {
    case 'mailgun':
      return [
        { key: 'apiKey', label: 'API key', secret: true, placeholder: 'key-...' },
        { key: 'domain', label: 'Sending domain', placeholder: 'mg.example.com' },
      ];
    case 'snowflake':
      return [
        { key: 'account', label: 'Account', placeholder: 'xy12345.us-east-1' },
        { key: 'user', label: 'User' },
        { key: 'password', label: 'Password', secret: true },
      ];
    case 'auth0':
      return [
        { key: 'domain', label: 'Tenant domain', placeholder: 'acme.auth0.com' },
        { key: 'apiKey', label: 'Management API token', secret: true },
      ];
    default:
      return [{ key: 'apiKey', label: 'API key', secret: true, placeholder: 'Paste your API key' }];
  }
}
